import { bookingRefundOffers } from "../../../app/api/cancelBooking";
import { nolOffersForLegs } from "../../../Lib/Utils/nolCard";
import { cancelLegsForDirection } from "../../../Components/OnwardTravel/bookedNolForLeg";
import { nolPassesBlockedByTicket } from "./nolPassesBlockedByTicket";
import { BaseAction } from "../../../core/actions/BaseAction";
import { fulfillmentIdSet, isFulfilledStatus, isRefundableFulfillment } from "../../../Lib/Utils/refundableFulfillments";
import { addRefundTotals, IRefundTotals, sumRefundOffers } from "../../../Lib/Utils/refundMoney";
import { updateRecoilState } from "../../../Orchestrator/Recoils/Events/Recoil";
import { appStore } from "../../../Orchestrator/Recoils/store";
import {
    selectedPassengerForCancelAtom,
    selectedPassengerForCancelInBoundAtom,
} from "../../../Orchestrator/Recoils/PassengerAtoms";
import { TripTypeEnum } from "../../../Orchestrator/Types/Enums/globalDefaultEnum";
import { IBookedOffer, IBookingResponse } from "../../../Orchestrator/Types/Index";
import { IRefundOfferRequest } from "../../../Orchestrator/Types/Types/BookingTypes";

/** The atoms hold bare ids on some paths and `{passengerId}` on others. */
const asPassengerIds = (value: unknown): string[] =>
    (Array.isArray(value) ? value : [])
        .map((entry: any) => (typeof entry === "string" ? entry : entry?.passengerId))
        .filter((id: unknown): id is string => typeof id === "string" && !!id);

/** One direction's refund-offers call: which ids, priced on their own. */
interface IDirectionQuote {
    fulfillmentIds: string[];
    totals: IRefundTotals | null;
    /** The retailer was asked and did not give a usable answer. */
    failed: boolean;
}

class CancelBookingInitAction extends BaseAction<void, {
    booking: IBookingResponse,
    filteredOutboundData?: string[],
    filteredInboundData?: string[],
    outboundChecked?: boolean,
    inboundChecked?: boolean
}> {
    override async run(): Promise<void | null> {
        const { booking, outboundChecked = true, inboundChecked = true } = this.params!;

        /**
         * Clear FIRST, every time.
         *
         * ACPT-022: these atoms are written only inside a chain of success
         * guards (a non-ok response, an empty `refundOffers`, or nothing
         * refundable all fell through writing nothing), so a failed re-quote
         * left the PREVIOUS selection's fee rendered against the new one. On a
         * product whose refund fee had been removed from configuration that
         * showed AED 60 for a booking the backend charges nothing for.
         *
         * `refundFeeAtom` is also persisted (WhitelistedAtoms), so the stale
         * number could even be a previous booking's, restored from
         * localStorage on a refresh. Nulling here is what guarantees the
         * receipt shows a skeleton — and then either a real number or an
         * error — rather than an old one.
         */
        this.clearQuote();

        try {
            const outboundPassengerIds = outboundChecked
                ? asPassengerIds(appStore.get(selectedPassengerForCancelAtom))
                : [];
            const inboundPassengerIds = inboundChecked
                ? asPassengerIds(appStore.get(selectedPassengerForCancelInBoundAtom))
                : [];

            /**
             * ACPT-023: the selection is read from the ATOMS, not from the
             * event payload.
             *
             * The payload's `filteredOutboundData`/`filteredInboundData` cannot
             * answer the question. They were consumed as `if (outbound) … else
             * if (inbound) … else EVERY PASSENGER ON THE BOOKING`, so:
             *
             *  - the two directions were mutually exclusive — an inbound-only
             *    selection was quoted against outbound offers, and vice versa;
             *  - `MainOutBoundCheckAction` sends `filteredInboundData: []` even
             *    while inbound is ticked, so the inbound half was routinely
             *    dropped;
             *  - and every "empty" payload — unticking a trip, or unticking the
             *    last passenger — fell into the `else` and priced the WHOLE
             *    booking. One Adult selected, four admissions quoted: AED 240.
             *
             * The atoms are what `CancelTripRefundTicket.handleOk` builds the
             * COMMIT from (gated on the same two checkbox flags), so quoting
             * from them is the only way the two can agree. Every emitter writes
             * them before emitting this event.
             */
            if (outboundPassengerIds.length === 0 && inboundPassengerIds.length === 0) {
                // Nothing selected is a real, priced answer: nothing is being
                // cancelled, so there is nothing to charge and nothing to
                // refund. Left at null the receipt would keep spinning.
                updateRecoilState("refundFeeAtom", { currency: "AED", amount: 0, scale: 2 });
                updateRecoilState("refundableAmountAtom", { currency: "AED", amount: 0, scale: 2 });
                return;
            }

            /**
             * One call PER DIRECTION, never one call for both.
             *
             * NOT because a spanning body is rejected — it is not. Probed live
             * on INT 2026-09-18 (booking J96JUY): four admission ids across two
             * trips in ONE body answered 200 with a single offer and a four-item
             * `refundOfferBreakDown`. The backend's own settlement path groups
             * by BOOKING and does the same (er-transcity,
             * `apps/@sds/beef/handlers/payment_ancillary_refund.go`: "grouped by
             * booking so we issue one refund-offers call per booking"). The
             * `RefundLeg` symbol this comment used to cite does not exist
             * anywhere in that repo.
             *
             * The real reason is `alreadyQuoted`, below. A Nol day pass covers
             * the legs of BOTH directions, so it matches each one; quoting it
             * twice double-counts the fee, and on the COMMIT path the second
             * call re-sends an id the first already retired — which the retailer
             * DOES reject, taking the whole inbound leg with it (ACPT-018).
             * Splitting by direction is what makes a shared pass attributable to
             * exactly one call, and it mirrors what the COMMIT does:
             * `ConfirmCancelClickAction` already issues one release+refund per
             * direction.
             *
             * `alreadyQuoted` spans the two calls. A Nol day pass covers the
             * legs of BOTH directions, so it matches each one — quoting it
             * twice both double-counts the fee and puts the same id in two
             * bodies. First direction to claim it wins, exactly as the commit
             * now does.
             */
            const alreadyQuoted = new Set<string>();
            const outbound = await this.quoteDirection(
                booking, TripTypeEnum.OUT_BOUND, outboundPassengerIds, alreadyQuoted
            );
            const inbound = await this.quoteDirection(
                booking, TripTypeEnum.IN_BOUND, inboundPassengerIds, alreadyQuoted
            );

            const sent = outbound.fulfillmentIds.length + inbound.fulfillmentIds.length;
            if (sent === 0) {
                // Passengers are selected but hold nothing refundable — already
                // refunded, or never fulfilled. Zero is the truthful figure.
                updateRecoilState("refundFeeAtom", { currency: "AED", amount: 0, scale: 2 });
                updateRecoilState("refundableAmountAtom", { currency: "AED", amount: 0, scale: 2 });
                return;
            }

            const totals = addRefundTotals(outbound.totals, inbound.totals);
            /**
             * A quote we could not establish must NOT read as AED 0.
             *
             * `sumRefundOffers` returns null for a declined quote, a missing
             * amount, or offers we cannot add — see its contract. Coercing any
             * of those to zero would show the passenger a free cancellation and
             * let them confirm one the retailer will charge for.
             */
            if (outbound.failed || inbound.failed || !totals) {
                this.failQuote("refund-offers did not price the selection");
                return;
            }

            updateRecoilState("refundFeeAtom", totals.refundFee);
            updateRecoilState("refundableAmountAtom", totals.refundableAmount);
        } catch (error: any) {
            // Never swallowed into a stale number: the receipt shows the error
            // state, so the failure is visible rather than mispriced.
            this.failQuote(error);
        }
    }

    private clearQuote() {
        updateRecoilState("refundOfferResponseAtom", null);
        updateRecoilState("refundFeeAtom", null);
        updateRecoilState("refundableAmountAtom", null);
        updateRecoilState("refundQuoteFailedAtom", false);
    }

    private failQuote(reason: unknown) {
        console.error("Cancel booking: could not price the refund:", reason);
        updateRecoilState("refundFeeAtom", null);
        updateRecoilState("refundableAmountAtom", null);
        updateRecoilState("refundQuoteFailedAtom", true);
    }

    /**
     * Price ONE direction for ONE set of passengers.
     *
     * `alreadyQuoted` is shared across directions and mutated here — see the
     * Nol note in `run()`.
     */
    private quoteDirection = async (
        booking: IBookingResponse,
        direction: TripTypeEnum.OUT_BOUND | TripTypeEnum.IN_BOUND,
        passengerIds: string[],
        alreadyQuoted: Set<string>
    ): Promise<IDirectionQuote> => {
        const empty: IDirectionQuote = { fulfillmentIds: [], totals: null, failed: false };
        if (passengerIds.length === 0) return empty;

        const trips = (booking?.booking?.trips || []).filter(
            (trip: any) => trip?.direction === direction
        );
        if (trips.length === 0) return empty;

        const tripIds = new Set(trips.map((trip: any) => trip.id));
        const offers: IBookedOffer[] = (booking?.booking?.bookedOffers || []).filter(
            (offer: any) => offer?.tripCoverage?.coveredTripId
                && tripIds.has(offer.tripCoverage.coveredTripId)
        );

        // A NOL pass sits on its OWN booked offer with NO tripCoverage, so the
        // direction filter above never selects it and the cancellation refunded
        // the train while silently keeping the pass. Its ADMISSION is what names
        // the legs, so select by those.
        // Widened past the trips still on the booking — see `cancellableLegIds`.
        // Identical derivation to the commit path, deliberately shared.
        const legIds = cancelLegsForDirection(booking?.booking as any, direction);
        const selectedOfferIds = new Set(offers.map((offer: any) => offer.offerId));
        for (const nolOffer of nolOffersForLegs(booking.booking, legIds)) {
            if (!selectedOfferIds.has(nolOffer.offerId)) offers.push(nolOffer);
        }

        const fulfillmentIds = fulfillmentIdSet(booking?.booking);
        // A pass follows its ticket — same exclusion the commit makes.
        const blockedNol = await nolPassesBlockedByTicket(booking, legIds, passengerIds);
        const ids: string[] = [];
        const take = (id: string | undefined) => {
            if (!id || alreadyQuoted.has(id) || blockedNol.has(id)) return;
            alreadyQuoted.add(id);
            ids.push(id);
        };

        for (const offer of offers) {
            for (const admission of offer?.admissions || []) {
                // `passengerIds` is nullable on an admission (docs/retailer.json,
                // BookingSnapshotAdmission) — an unattributed one belongs to
                // nobody we selected and stays out.
                const isSelected = (admission?.passengerIds || []).some(
                    (pid: string) => passengerIds.includes(pid)
                );
                // FULFILLED only, and present in `booking.fulfillments`. The
                // SAME predicate the commit uses — the two paths disagreeing is
                // what shipped the earlier shuttle bug.
                if (isSelected && isRefundableFulfillment(admission, fulfillmentIds)) {
                    take(admission.id);
                }
            }
            /**
             * Ancillaries (addons) belonging to the selected passengers.
             *
             * No `booking.fulfillments` membership check — an ancillary need not
             * appear in that array (Nol never does) while the refund endpoints
             * still accept its id.
             */
            for (const ancillary of (offer?.ancillaries || []) as any[]) {
                const isSelected = (ancillary?.passengerIds || []).some(
                    (pid: string) => passengerIds.includes(pid)
                );
                if (isSelected && isFulfilledStatus(ancillary)) take(ancillary.id);
            }
        }

        if (ids.length === 0) return empty;

        const request: IRefundOfferRequest = {
            fulfillmentIds: ids,
            overruleCode: null,
            refundDate: new Date().toISOString()
        };
        const response: any = await bookingRefundOffers(request, booking?.booking?.id);
        if (response?.status !== "ok" || !response?.answer) {
            return { fulfillmentIds: ids, totals: null, failed: true };
        }

        updateRecoilState("refundOfferResponseAtom", response.answer);
        // Sums EVERY offer in the array. Reading `refundOffers[0]` quoted the
        // first booked offer only and dropped the rest — see sumRefundOffers.
        const totals = sumRefundOffers(response.answer?.refundOffers);
        return { fulfillmentIds: ids, totals, failed: !totals };
    }
}

export const cancelBookingInitAction = new CancelBookingInitAction()
